import { useEffect, useMemo, useRef, useState } from "react";
import { Alert, Button, Divider, Drawer, Form, Input, InputNumber, Modal, Radio, Space, Typography } from "antd";
import { toast } from "react-toastify";
import { useAuth } from "contexts/AuthContext";
import useRefunds from "hooks/finance/useRefunds";

const formatCurrency = (amount, currency = "USD") => {
  const numericAmount = Number(amount);
  if (!Number.isFinite(numericAmount)) return "-";

  const currencyCode = (currency || "USD").toUpperCase();
  try {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: currencyCode,
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(numericAmount);
  } catch {
    return `${numericAmount.toFixed(2)} ${currencyCode}`;
  }
};

export default function IssueRefundDrawer({
  open,
  onClose,
  transaction,
  onRefundCreated,
}) {
  const { user } = useAuth();
  const { summary, loadingSummary, loadingCreate, fetchRefundSummary, createRefund, toMoney } = useRefunds();

  const [form] = Form.useForm();
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [pendingValues, setPendingValues] = useState(null);
  const correlationIdRef = useRef(null);

  const transactionId = transaction?.id || null;

  useEffect(() => {
    if (!open) return;
    if (!transactionId) return;
    fetchRefundSummary(transactionId);
  }, [open, transactionId, fetchRefundSummary]);

  const computedSummary = useMemo(() => {
    if (!summary) return null;
    return {
      ...summary,
      originalAmountMoney: toMoney(summary.originalAmount),
      refundedAmountMoney: toMoney(summary.refundedAmount),
      refundableRemainingAmountMoney: toMoney(summary.refundableRemainingAmount),
    };
  }, [summary, toMoney]);

  const currency = computedSummary?.currency || transaction?.currency || "USD";
  const refundableRemaining = Number(computedSummary?.refundableRemainingAmountMoney ?? 0);
  const refundedAlready = Number(computedSummary?.refundedAmountMoney ?? 0);
  const originalAmount = Number(computedSummary?.originalAmountMoney ?? 0);

  useEffect(() => {
    if (!open) return;
    if (!computedSummary) return;
    correlationIdRef.current = typeof crypto !== "undefined" ? crypto.randomUUID() : null;
    form.resetFields();
    form.setFieldsValue({
      refundType: "FULL",
      amount: refundableRemaining,
      reason: "",
    });
  }, [open, computedSummary, form, refundableRemaining]);

  const canRefund = Boolean(computedSummary?.refundable);
  const notRefundableReason = computedSummary?.notRefundableReason || null;

  const validateAmount = (_, value) => {
    const refundType = form.getFieldValue("refundType");
    const numericValue = Number(value);

    if (refundType === "FULL") return Promise.resolve();
    if (!Number.isFinite(numericValue)) return Promise.reject(new Error("Enter a valid amount."));
    if (numericValue <= 0) return Promise.reject(new Error("Refund amount must be greater than 0."));
    if (numericValue > refundableRemaining) return Promise.reject(new Error("Refund amount exceeds available refundable amount."));
    return Promise.resolve();
  };

  const openConfirm = async () => {
    if (!transactionId) return;
    let values;
    try {
      values = await form.validateFields();
    } catch {
      // AntD throws a non-Error object when validation fails; avoid surfacing
      // it as an uncaught runtime error and let the form show field errors.
      return;
    }
    const refundType = values.refundType;
    const refundAmount = refundType === "FULL" ? refundableRemaining : Number(values.amount);
    const remainingAfter = Math.max(0, refundableRemaining - refundAmount);

    setPendingValues({
      refundType,
      refundAmount,
      remainingAfter,
      reason: values.reason,
    });
    setConfirmOpen(true);
  };

  const submitRefund = async () => {
    if (!pendingValues || !transactionId) return;

    const actorId =
      user?.id || sessionStorage.getItem("actorId") || sessionStorage.getItem("userId");

    try {
      const correlationId = correlationIdRef.current || (typeof crypto !== "undefined" ? crypto.randomUUID() : null);
      correlationIdRef.current = correlationId;

      await createRefund({
        transactionId,
        refundType: pendingValues.refundType,
        amount: pendingValues.refundAmount,
        reason: pendingValues.reason,
        actorId,
        correlationId,
      });

      toast.success("Refund created successfully");
      await fetchRefundSummary(transactionId);
      await onRefundCreated?.({ transactionId });
      setConfirmOpen(false);
      setPendingValues(null);
      onClose();
    } catch {
      // `useRefunds` already shows a toast error; keep the drawer open to retry.
    }
  };

  const handleRefundTypeChange = (e) => {
    const nextType = e?.target?.value;
    if (nextType === "FULL") {
      form.setFieldsValue({ amount: refundableRemaining });
    }
  };

  return (
    <Drawer
      open={open}
      onClose={onClose}
      title="Issue Refund"
      width={520}
      destroyOnClose
      zIndex={9999}
      footer={
        <Space style={{ display: "flex", justifyContent: "flex-end" }}>
          <Button onClick={onClose} disabled={loadingCreate}>
            Cancel
          </Button>
          {canRefund ? (
            <Button type="primary" onClick={openConfirm} loading={loadingCreate}>
              Issue Refund
            </Button>
          ) : null}
        </Space>
      }
    >
      {!computedSummary ? (
        <Alert
          type="info"
          showIcon
          message={loadingSummary ? "Loading refund eligibility..." : "Refund eligibility not available"}
        />
      ) : (
        <>
          {!canRefund ? (
            <Alert
              type="warning"
              showIcon
              message="Transaction not eligible for refund"
              description={notRefundableReason || "Refunding is not allowed for this transaction."}
              style={{ marginBottom: 12 }}
            />
          ) : null}

          <div style={{ display: "flex", gap: 12, flexWrap: "wrap", marginBottom: 12 }}>
            <div style={{ minWidth: 150 }}>
              <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                Original
              </Typography.Text>
              <div style={{ fontWeight: 600 }}>{formatCurrency(originalAmount, currency)}</div>
            </div>
            <div style={{ minWidth: 150 }}>
              <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                Refunded
              </Typography.Text>
              <div style={{ fontWeight: 600 }}>{formatCurrency(refundedAlready, currency)}</div>
            </div>
            <div style={{ minWidth: 150 }}>
              <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                Remaining
              </Typography.Text>
              <div style={{ fontWeight: 600 }}>{formatCurrency(refundableRemaining, currency)}</div>
            </div>
          </div>

          <Divider style={{ margin: "12px 0" }} />

          <Form form={form} layout="vertical" disabled={!canRefund || loadingCreate}>
            <Form.Item name="refundType" label="Refund Type" rules={[{ required: true }]}>
              <Radio.Group onChange={handleRefundTypeChange}>
                <Radio value="FULL">Full Refund</Radio>
                <Radio value="PARTIAL">Partial Refund</Radio>
              </Radio.Group>
            </Form.Item>

            <Form.Item shouldUpdate={(prev, next) => prev.refundType !== next.refundType} noStyle>
              {({ getFieldValue }) => {
                const refundType = getFieldValue("refundType");
                return (
                  <Form.Item
                    name="amount"
                    label="Refund Amount"
                    rules={[{ validator: validateAmount }]}
                    extra={
                      refundType === "FULL"
                        ? `Refundable remaining amount: ${formatCurrency(refundableRemaining, currency)}`
                        : `Max refundable: ${formatCurrency(refundableRemaining, currency)}`
                    }
                  >
                    <InputNumber
                      min={0}
                      precision={2}
                      style={{ width: "100%" }}
                      disabled={refundType === "FULL"}
                    />
                  </Form.Item>
                );
              }}
            </Form.Item>

            <Form.Item
              name="reason"
              label="Reason for Refund"
              rules={[{ required: true, message: "Refund reason is required." }]}
            >
              <Input.TextArea rows={3} placeholder="Explain why the refund is being issued..." />
            </Form.Item>
          </Form>
        </>
      )}

      <Modal
        open={confirmOpen}
        title="Refund Confirmation"
        destroyOnClose
        centered
        zIndex={11000}
        getContainer={false}
        maskClosable={!loadingCreate}
        onCancel={() => (loadingCreate ? null : setConfirmOpen(false))}
        footer={
          <Space style={{ display: "flex", justifyContent: "flex-end" }}>
            <Button onClick={() => setConfirmOpen(false)} disabled={loadingCreate}>
              Back
            </Button>
            <Button danger type="primary" onClick={submitRefund} loading={loadingCreate}>
              Confirm & Issue
            </Button>
          </Space>
        }
      >
        {pendingValues ? (
          <div style={{ lineHeight: 1.9 }}>
            <div>
              <strong>Original transaction amount:</strong> {formatCurrency(originalAmount, currency)}
            </div>
            <div>
              <strong>Amount already refunded:</strong> {formatCurrency(refundedAlready, currency)}
            </div>
            <div>
              <strong>Refund amount being issued:</strong> {formatCurrency(pendingValues.refundAmount, currency)}
            </div>
            <div>
              <strong>Remaining amount after refund:</strong> {formatCurrency(pendingValues.remainingAfter, currency)}
            </div>
            <div>
              <strong>Refund type:</strong> {String(pendingValues.refundType || "-").toUpperCase()}
            </div>
            <div>
              <strong>Reason:</strong> {pendingValues.reason || "-"}
            </div>
          </div>
        ) : null}
      </Modal>
    </Drawer>
  );
}
